// src/services/catalogApi.js
//
// Katalog v2 (/v2/...) uçlarının tek toplandığı yer.
// Eski uçlar (/countries-data, /regions-data, /lifetime-data …) değişmedi; burası
// yalnızca yeni şema (bundle + plan) için EK bir yüzeydir.
//
// İki kural bu dosyanın her yerine sinmiştir:
//   1) Sunucu null alanları JSON'a hiç koymaz. Bir alanın yokluğu "null" demektir,
//      "hata" demek değildir. Özellikle `dataMb` yoksa paket sınırsızdır.
//   2) Hata ayrımı `error` metnine göre değil `code` alanına göre yapılır.

import apiClient from './ApiClient';

// --- Sabitler -------------------------------------------------------------

// Ürün ailesi: hangi ürün hattı. Yeni aile geldiğinde yeni endpoint açılmaz,
// buraya yeni bir değer eklenir.
export const FAMILIES = [
    { value: 'unlimited', label: 'Sınırsız' },
    { value: 'standard', label: 'Standart' },
    { value: 'lifetime', label: 'Ön Ödemeli' },
    { value: 'one', label: 'One' },
    { value: 'voice', label: 'Ses' },
];

// Kapsam: paket kaç ülkeyi kapsıyor.
export const SCOPES = [
    { value: 'country', label: 'Ülke' },
    { value: 'regional', label: 'Bölge' },
    { value: 'global', label: 'Global' },
];

// Form faktörü: eSIM mi fiziksel SIM mi.
export const FORM_FACTORS = [
    { value: 'esim', label: 'eSIM' },
    { value: 'psim', label: 'Fiziksel SIM' },
];

// Sync edilebilir katalog dilimleri.
// `physical` sunucu tarafından bilerek reddedilir (400): fiziksel SIM eSIM
// provizyon zinciriyle uyumlu değil. Kullanıcıyı 400'e yürütmemek için
// listede ama seçilemez halde duruyor.
export const SYNC_CATALOGS = [
    { value: 'unlimited', label: 'Sınırsız', note: 'Yeni şemaya eklenir.' },
    { value: 'country', label: 'Ülke', note: 'Eski şemada zaten olan paketler reddedilir.' },
    { value: 'regional', label: 'Bölge', note: 'Eski şemada zaten olan paketler reddedilir.' },
    { value: 'global', label: 'Global', note: 'Eski şemada zaten olan paketler reddedilir.' },
    { value: 'lifetime', label: 'Ön Ödemeli', note: 'Eski şemada zaten olan paketler reddedilir.' },
    { value: 'physical', label: 'Fiziksel SIM', note: 'Desteklenmiyor — sunucu reddeder.', disabled: true },
];

export const familyLabel = (value) =>
    FAMILIES.find((f) => f.value === value)?.label || value || '—';

export const scopeLabel = (value) =>
    SCOPES.find((s) => s.value === value)?.label || value || '—';

export const formFactorLabel = (value) =>
    FORM_FACTORS.find((f) => f.value === value)?.label || value || '—';

// --- Hata kodları --------------------------------------------------------

// Metinler değişebilir, kodlar sözleşmedir. Lokalizasyon buradan yapılır.
const ERROR_MESSAGES = {
    INVALID_FAMILY: 'Tanınmayan ürün ailesi. Filtreyi kontrol edin.',
    INVALID_SCOPE: 'Tanınmayan kapsam. Filtreyi kontrol edin.',
    INVALID_PRICE: 'Fiyat geçersiz. Pozitif bir sayı girin.',
    PRICE_REQUIRED: 'Bu planın taban fiyatı yok; mağaza yayın durumu değiştirilemez. Önce fiyat girin.',
    STORE_PUSH_FAILED: 'Google Play\'e ulaşılamadı. Hiçbir alan yazılmadı — tekrar deneyin.',
    BUNDLE_NOT_PRICED: 'Hiçbir planında fiyat yok; yayına alınamaz. Önce en az bir plana fiyat girin.',
    // Beklemek çözmez: "tekrar deneyin" demek bu kodda yanlış tavsiye olur.
    PLAY_QUOTA_EXCEEDED: 'Google Play ürün sınırı dolu (1000/1000). Yeni ürün yayınlanamıyor — önce eski ürünler temizlenmeli.',
};

/**
 * Hata gövdesinden { code, message } üretir.
 * Sıra: bilinen `code` → sunucunun `error`/`message` metni → HTTP durumu → fallback.
 */
export const readApiError = (err, fallback = 'Beklenmeyen bir hata oluştu.') => {
    const data = err?.response?.data;
    const code = data?.code || null;

    if (code && ERROR_MESSAGES[code]) {
        return { code, message: ERROR_MESSAGES[code] };
    }

    const text = data?.error || data?.message;
    if (text) return { code, message: text };

    const status = err?.response?.status;
    if (status === 404) return { code, message: 'Kayıt bulunamadı.' };
    if (status === 403) return { code, message: 'Bu işlem için yetkiniz yok.' };

    return { code, message: fallback };
};

// BaseResponse zarfı: { status?, message?, data, error?, code? }.
// Bazı uçlar düz gövde döndürebildiği için ikisini de tolere ediyoruz.
export const unwrap = (responseData) => {
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        return responseData.data;
    }
    return responseData;
};

// --- Görüntüleme yardımcıları -------------------------------------------

/**
 * Veri limiti etiketi. KARAR `isUnlimited` ALANINA GÖRE VERİLİR — `dataMb`'nin
 * varlığına göre değil. Sağlayıcı sınırsız planlara amount_mb=1024 yazıyor;
 * o değer gerçek bir limit olmadığı için backend `dataMb`'yi bilerek boş
 * bırakıyor. Gösterirsek müşteriye "1 GB" demiş oluruz.
 */
export const formatDataAmount = (plan) => {
    if (!plan) return '—';
    if (plan.isUnlimited) return 'Sınırsız';
    const mb = Number(plan.dataMb);
    if (!Number.isFinite(mb)) return '—';
    if (mb >= 1024) {
        const gb = mb / 1024;
        return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
    }
    return `${mb} MB`;
};

/**
 * Konuşma/SMS etiketi (ses paketleri). `-1` SINIRSIZ demektir; sayı olarak
 * basılırsa ekranda "−1 dakika" yazar. Alan yoksa veya 0 ise paketin o hakkı
 * yok demektir — satırda hiç yer kaplamasın diye null döner.
 */
export const formatVoiceSms = (plan) => {
    const read = (value) => {
        const n = Number(value);
        return Number.isFinite(n) && n !== 0 ? n : null;
    };
    const voice = read(plan?.voiceMinutes);
    const sms = read(plan?.smsCount);

    if (voice === null && sms === null) return null;
    if (voice === -1 && sms === -1) return 'Sınırsız konuşma & SMS';

    const parts = [];
    if (voice !== null) parts.push(voice === -1 ? 'Sınırsız konuşma' : `${voice} dk`);
    if (sms !== null) parts.push(sms === -1 ? 'Sınırsız SMS' : `${sms} SMS`);
    return parts.join(' + ');
};

// Para birimi alanları string olarak gelir ("199.90"); güvenli formatlama.
export const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const n = parseFloat(value);
    return Number.isNaN(n) ? '—' : n.toFixed(2);
};

// Kaba marj yüzdesi. Kur çevirmesi gerekmemesi için iki tarafı da USD alıyoruz:
// satış = Google'ın ürettiği `priceUsd`, maliyet = sağlayıcının `providerPriceUsd`.
// providerPriceUsd her yanıtta gelmeyebilir (null alanlar serialize edilmez);
// gelmiyorsa null döner ve gösterge çizilmez.
export const marginPercent = (priceUsd, providerPriceUsd) => {
    const sale = parseFloat(priceUsd);
    const cost = parseFloat(providerPriceUsd);
    if (!Number.isFinite(sale) || !Number.isFinite(cost) || cost <= 0) return null;
    return ((sale - cost) / cost) * 100;
};

/** Bir bundle yayına alınabilir mi? En az bir planında taban fiyat olmalı. */
export const hasPricedPlan = (bundle) =>
    (bundle?.plans || []).some((plan) => {
        const n = parseFloat(plan.basePriceTry);
        return Number.isFinite(n) && n > 0;
    });

/**
 * Eski paket sayfalarının (Paketler / Bölgesel / Ömürlük) düzenleme durumunu
 * sunucudaki kayıtla karşılaştırıp YALNIZCA DEĞİŞEN alanları içeren patch üretir.
 *
 * Üç sayfa da aynı alanları düzenliyor; diff mantığı tek yerde dursun diye burada.
 * `basePriceTry` string gönderilir (uç string bekler) ve karşılaştırma sayı üzerinden
 * yapılır: "49.90" ile 49.9 aynı fiyattır, biçim farkı isteğe dönüşmemeli.
 */
export const buildPlanPatchFromEdit = (edited, original) => {
    const patch = {};
    if (!edited || !original) return patch;

    const next = parseFloat(edited.basePriceTry);
    const current = parseFloat(original.basePriceTry);
    if (Number.isFinite(next) && next !== current) {
        patch.basePriceTry = String(next);
    }

    ['isOpenGp', 'isOpenAp', 'isOpenWeb'].forEach((key) => {
        if (!!edited[key] !== !!original[key]) patch[key] = !!edited[key];
    });

    return patch;
};

/** Planlar `sortOrder` ile sıralanır. Veri miktarına göre SIRALAMA YAPMA —
 *  sınırsız planların hepsinde eşit olur. */
export const sortPlans = (plans = []) =>
    [...plans].sort((a, b) => {
        const ao = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : Number.MAX_SAFE_INTEGER;
        const bo = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return (Number(a.validityDays) || 0) - (Number(b.validityDays) || 0);
    });

// --- Uçlar ---------------------------------------------------------------

/**
 * GET /v2/admin/bundles
 * Public uçtan farkları: yayına alınmamış bundle'lar da döner, fiyatsız planlar
 * da döner, platform filtresi uygulanmaz.
 * Boş/undefined filtreler gönderilmez (boş değer sunucuda "filtre yok" demek).
 *
 * `includeInactive: false` yalnızca aktifleri getirir. Parametre verilmezse
 * sunucu hepsini döner (varsayılan); diğer çağıranların davranışı değişmesin
 * diye yalnızca açıkça false verildiğinde gönderilir.
 *
 * `continentId` panelin kıta kimliği (0 Global … 7 Pasifik). Bir kıta birden çok
 * sağlayıcı bölgesine karşılık gelebildiği için (Latin Amerika = South America +
 * Central America + Caribbean) eşlemeyi sunucu yapar; tek değerli `region`
 * bunun yerine geçmez. 0 geçerli bir değer — `if (continentId)` onu düşürürdü.
 */
export const fetchAdminBundles = async ({ family, scope, country, region, continentId, includeInactive } = {}) => {
    const params = {};
    if (includeInactive === false) params.includeInactive = false;
    if (family) params.family = family;
    if (scope) params.scope = scope;
    if (country) params.country = country;
    if (region) params.region = region;
    if (continentId !== undefined && continentId !== null) params.continentId = continentId;

    const response = await apiClient.get('/v2/admin/bundles', { params });
    const data = unwrap(response.data);
    return Array.isArray(data) ? data : [];
};

/**
 * POST /v2/admin/catalog/sync
 * Yalnızca ekleme yapar: hiçbir satır silinmez, hiçbir fiyat güncellenmez.
 * `dryRun` gövdede yoksa sunucu DAİMA dry run yapar; yazmak için açıkça false.
 */
export const syncCatalog = async (catalog, { dryRun = true } = {}) => {
    const response = await apiClient.post('/v2/admin/catalog/sync', { catalog, dryRun });
    return unwrap(response.data);
};

/**
 * PATCH /v2/admin/bundle-plans/{id}
 * Tüm alanlar opsiyonel; GÖNDERİLMEYEN ALANA DOKUNULMAZ. Bu yüzden çağıran
 * taraf yalnızca değişen alanları göndermelidir — aksi halde sadece sıra
 * değiştirmek bile Google Play'e gereksiz bir fiyat push'u tetikler.
 */
export const updateBundlePlan = async (planId, patch) => {
    const response = await apiClient.patch(`/v2/admin/bundle-plans/${planId}`, patch);
    return unwrap(response.data);
};

/**
 * PATCH /v2/admin/bundles/{id}
 * isActive / title / description / imgUrl. Fiyatsız bundle yayına alınamaz:
 * 409 + code BUNDLE_NOT_PRICED.
 */
export const updateBundle = async (bundleId, patch) => {
    const response = await apiClient.patch(`/v2/admin/bundles/${bundleId}`, patch);
    return unwrap(response.data);
};

/**
 * POST /v2/admin/bundles/{id}/publish-play
 *
 * Bundle'ı Google Play'e TEK ürün olarak yayınlar (`bundle_{id}`); fiyatı girilmiş
 * her planı o ürünün bir purchase option'ı olur. Doğru sıra: fiyat → yayınla →
 * yayına al. Google plan başına referans fiyat döndürür ve sunucu bunları kaydeder.
 *
 * Bundle pasifken de çağrılabilir — yayınlamak müşteriye göstermez, yalnızca
 * mağaza ürününü hazırlar. Fiyatı girilmemiş planlar yayına DAHİL EDİLMEZ.
 */
export const publishBundleToPlay = async (bundleId) => {
    // Yayın, Google tarafında dakikaya yaklaşabiliyor (bundle + N option + bölgesel
    // fiyatlar). Genel 20 sn'lik axios zaman aşımı burada bağlantıyı koparıyor ve
    // sunucudaki yayını da yarıda kesebiliyor — bundle_700 iki kez böyle düştü.
    // Sunucu Google'a 120 sn'ye kadar bekliyor; istemci ondan uzun beklemeli.
    const response = await apiClient.post(
        `/v2/admin/bundles/${bundleId}/publish-play`,
        null,
        { timeout: 150000 },
    );
    return unwrap(response.data);
};
